/**
 * Orders API Transformers
 * Pure field mapping functions - no business logic
 */

import {
  normalizeEntity,
  normalizeRelation,
  toDateOnly,
  parseNumber,
  isRecord,
} from '@spcc/core'

import type {
  Order,
  OrderStatus,
  OrderStats,
  OrdersFilter,
  LocalizedText,
  Quantity,
} from '../domain/types'

import {
  DEFAULT_CURRENCY,
  DEFAULT_PRODUCT_NAME,
  API_ORDER_STATUS_TO_DOMAIN,
  ORDER_NUMBER_PREFIX,
} from '../domain/constants'

// ============================================
// Helper Functions
// ============================================

function mapApiStatusToDomain(apiStatus: string): OrderStatus {
  return (API_ORDER_STATUS_TO_DOMAIN[apiStatus as keyof typeof API_ORDER_STATUS_TO_DOMAIN] ?? 'pending') as OrderStatus
}

function inferPackaging(variant: Record<string, unknown> | null): 'bags' | 'bulk' {
 return variant?.packaging as 'bags' | 'bulk' | undefined ?? 'bags'
}

function normalizeLocalizedText(value: unknown): LocalizedText {
  if (typeof value === 'string') {
    return { en: value, ar: value }
  }
  if (typeof value === 'object' && value !== null) {
    const obj = value as Record<string, unknown>
    return {
      en: typeof obj.en === 'string' ? obj.en : '',
      ar: typeof obj.ar === 'string' ? obj.ar : (typeof obj.en === 'string' ? obj.en : ''),
    }
  }
  return DEFAULT_PRODUCT_NAME
}

function coerceNonEmptyString(value: unknown): string | null {
  if (typeof value !== 'string') return null
  const trimmed = value.trim()
  return trimmed || null
}

/** Join Strapi location-style fields; falls back to location `name` when lines are empty. */
function buildAddressLineFromLocationRecord(
  loc: Record<string, unknown> | null | undefined
): string {
  if (!loc) return ''
  const parts = [loc.addressLine1, loc.addressLine2, loc.postalCode].filter(Boolean) as string[]
  const joined = parts.join(', ')
  if (joined) return joined
  return coerceNonEmptyString(loc.name) ?? ''
}

function extractVehicleLicensePlateFromRawEntity(rawEntity: Record<string, unknown> | null): string | undefined {
  const attributes = rawEntity?.attributes as Record<string, unknown> | undefined

  const directCandidates: unknown[] = [
    rawEntity?.vehicleLicensePlate,
    attributes?.vehicleLicensePlate,
    rawEntity?.vehiclePlate,
    attributes?.vehiclePlate,
    rawEntity?.licensePlate,
    attributes?.licensePlate,
  ]

  for (const candidate of directCandidates) {
    const normalized = coerceNonEmptyString(candidate)
    if (normalized) return normalized
  }

  const rawResource = rawEntity?.resource ?? attributes?.resource
  const resource = normalizeRelation<Record<string, unknown>>(rawResource)
  const powerUnit = normalizeRelation<Record<string, unknown>>(resource?.powerUnit)
  const trailer = normalizeRelation<Record<string, unknown>>(resource?.trailer)

  const metadata = isRecord(rawEntity?.metadata)
    ? rawEntity?.metadata
    : isRecord(attributes?.metadata)
      ? attributes?.metadata
      : null

  const nestedCandidates: unknown[] = [
    powerUnit?.licensePlate,
    trailer?.licensePlate,
    metadata?.licensePlate,
  ]

  for (const candidate of nestedCandidates) {
    const normalized = coerceNonEmptyString(candidate)
    if (normalized) return normalized
  }

  return undefined
}

function toNullableNonNegativeInteger(value: unknown): number | null {
  if (value === null || value === undefined) return null
  const n = typeof value === 'number' ? value : Number(value)
  if (!Number.isFinite(n) || n < 0) return null
  return Math.trunc(n)
}

function generateOrderNumber(entity: Record<string, unknown>): string {
  const id = entity.id
  const base = id != null ? String(id) : ''
  return base ? `${ORDER_NUMBER_PREFIX}-${base.slice(0, 8).toUpperCase()}` : ORDER_NUMBER_PREFIX
}

// ============================================
// Order Transformer
// ============================================

export interface ApiOrderEntity {
  id?: number
  documentId?: string
  orderNumber?: string
  sapSalesOrderNumber?: string | null
  driverName?: unknown
  vehicleLicensePlate?: unknown
  variant?: unknown
  dropoff?: unknown
  tenant?: unknown
  sales_order?: unknown
  quantity?: unknown
  pricingSnapshot?: unknown
  metadata?: unknown
  orderStatus?: string
  processingStartedAt?: string
  deliveryDate?: string
  createdAt?: string
  updatedAt?: string
  cancellationReason?: string
  processingToDeliveredSeconds?: unknown
  loadingDurationSeconds?: unknown
  subCustomer?: unknown
  /** Injected by the export controller: sum of accepted DeliveryLine.deliveredQuantity for this order's shipment */
  deliveredQuantityFromLines?: unknown
}

// Sub-customer field extractors
function normalizeSubCustomerRelation(raw: unknown): Record<string, unknown> | null {
  if (!raw || typeof raw !== 'object') return null
  const r = raw as Record<string, unknown>
  // Strapi v5 flat format
  if (r.id != null || r.documentId != null) return r
  // Strapi v4 nested format
  if (r.data && typeof r.data === 'object') return (r.data as Record<string, unknown>)?.attributes as Record<string, unknown> | null
  return null
}

function extractSubCustomerId(raw: unknown): string | null {
  const sc = normalizeSubCustomerRelation(raw)
  if (!sc) return null
  return sc.id != null ? String(sc.id) : null
}

function extractSubCustomerDocumentId(raw: unknown): string | null {
  const sc = normalizeSubCustomerRelation(raw)
  if (!sc) return null
  return typeof sc.documentId === 'string' ? sc.documentId : null
}

function extractSubCustomerName(raw: unknown): string | null {
  const sc = normalizeSubCustomerRelation(raw)
  if (!sc) return null
  return typeof sc.name === 'string' ? sc.name : null
}

function extractSubCustomerPhone(raw: unknown): string | null {
  const sc = normalizeSubCustomerRelation(raw)
  if (!sc) return null
  return typeof sc.phone === 'string' ? sc.phone : null
}

function extractSubCustomerCode(raw: unknown): string | null {
  const sc = normalizeSubCustomerRelation(raw)
  if (!sc) return null
  return typeof sc.code === 'string' ? sc.code : null
}

export function transformOrderResponseToDomain(
  raw: unknown,
  salesOrderId?: string
): Order {
  // Check raw entity first for orderNumber before normalization
  const rawEntity = raw as Record<string, unknown> | null
  const rawOrderNumberFromEntity = rawEntity?.orderNumber ?? 
    (rawEntity?.attributes as Record<string, unknown> | undefined)?.orderNumber
  const rawDriverNameFromEntity = rawEntity?.driverName ??
    (rawEntity?.attributes as Record<string, unknown> | undefined)?.driverName
  const rawVehicleLicensePlateFromEntity = extractVehicleLicensePlateFromRawEntity(rawEntity)
  const rawProcessingToDeliveredSeconds =
    rawEntity?.processingToDeliveredSeconds ??
    (rawEntity?.attributes as Record<string, unknown> | undefined)?.processingToDeliveredSeconds
  const rawLoadingDurationSeconds =
    rawEntity?.loadingDurationSeconds ??
    (rawEntity?.attributes as Record<string, unknown> | undefined)?.loadingDurationSeconds
  const rawExitedFactoryAt =
    rawEntity?.exitedFactoryAt ??
    (rawEntity?.attributes as Record<string, unknown> | undefined)?.exitedFactoryAt

  const rawLoadingLocationLabel =
    rawEntity?.loadingLocationLabel ??
    (rawEntity?.attributes as Record<string, unknown> | undefined)?.loadingLocationLabel

  // Sub-customer extraction
  const rawSubCustomer =
    rawEntity?.subCustomer ??
    (rawEntity?.attributes as Record<string, unknown> | undefined)?.subCustomer

  const entity = normalizeEntity<ApiOrderEntity>(raw)
  if (!entity) {
    throw new Error('Invalid order entity')
  }

  const id = String(entity.documentId ?? entity.id ?? '')
  const variant = normalizeRelation<Record<string, unknown>>(entity.variant)
  const sku = normalizeRelation<Record<string, unknown>>(variant?.sku)
  const dropoff = normalizeRelation<Record<string, unknown>>(entity.dropoff)
  const tenant = normalizeRelation<Record<string, unknown>>(entity.tenant)
  const salesOrder = normalizeRelation<Record<string, unknown>>(entity.sales_order)
  const orderFactory = normalizeRelation<Record<string, unknown>>(entity.factory)
  // Prefer direct order factory, fallback to sales order factory for legacy data.
  const factory = orderFactory ?? normalizeRelation<Record<string, unknown>>(salesOrder?.factory)

  const pricingSnapshot = (entity.pricingSnapshot ?? {}) as Record<string, unknown>
  const metadata = (entity.metadata ?? {}) as Record<string, unknown>

  // Order number: check raw entity first, then normalized entity.orderNumber, then pricingSnapshot.orderNumber
  const rawOrderNumber = rawOrderNumberFromEntity ?? entity.orderNumber ?? pricingSnapshot.orderNumber
  let orderNumber: string
  
  if (rawOrderNumber != null && rawOrderNumber !== '') {
    const normalized = typeof rawOrderNumber === 'string'
      ? rawOrderNumber.trim()
      : String(rawOrderNumber).trim()
    
    // Only use if it's not empty and not '0'
    if (normalized && normalized !== '0') {
      orderNumber = normalized
    } else {
      // If it's empty or '0', use empty string (don't use ID)
      orderNumber = ''
    }
  } else {
    // If orderNumber is completely missing, use empty string (don't use ID)
    orderNumber = ''
  }

  // Packaging and unit
  const packaging = String(pricingSnapshot.packaging ?? inferPackaging(variant))
  const unit: Quantity['unit'] = packaging === 'bulk' ? 'tons' : 'bags'

  // Quantity
  const quantityValue = parseNumber(entity.quantity)

  // Unit price
  const unitPriceObj = pricingSnapshot.unitPrice as Record<string, unknown> | undefined
  const unitPriceAmount = parseNumber(unitPriceObj?.amount)

  // Product name
  const productName = variant?.name ?? pricingSnapshot.productName ?? sku?.name
  const productLocalized = normalizeLocalizedText(productName)

  // Dates
  const orderDate = toDateOnly(entity.createdAt)
  const deliveryDate = toDateOnly(entity.deliveryDate)

  // Delivery address: order dropoff / metadata, then sales order location + metadata (cityName, etc.)
  const dropoffCity = normalizeRelation<Record<string, unknown>>(dropoff?.city)
  const soMeta = salesOrder && isRecord(salesOrder.metadata) ? salesOrder.metadata : null
  const salesOrderDeliveryLoc = normalizeRelation<Record<string, unknown>>(salesOrder?.deliveryAddress)
  const salesOrderCityRel = normalizeRelation<Record<string, unknown>>(salesOrder?.city)

  const addressParts = [
    dropoff?.addressLine1,
    dropoff?.addressLine2,
    dropoff?.postalCode,
  ].filter(Boolean) as string[]

  const deliveryAddress =
    addressParts.join(', ') ||
    coerceNonEmptyString(metadata.deliveryAddress) ||
    buildAddressLineFromLocationRecord(salesOrderDeliveryLoc) ||
    coerceNonEmptyString(soMeta?.deliveryAddress) ||
    coerceNonEmptyString(soMeta?.cityName) ||
    ''

  const deliveryAddressId =
    dropoff?.documentId != null || dropoff?.id != null
      ? String(dropoff?.documentId ?? dropoff?.id)
      : salesOrderDeliveryLoc?.documentId != null || salesOrderDeliveryLoc?.id != null
        ? String(salesOrderDeliveryLoc?.documentId ?? salesOrderDeliveryLoc?.id)
        : undefined

  // rawEntity?.cityName is set by the export enrichment with the correct locale;
  // it takes priority over DB relation names which may carry the wrong locale.
  const dropoffCityName =
    coerceNonEmptyString(rawEntity?.cityName) ??
    coerceNonEmptyString(rawEntity?.city?.name) ??
    coerceNonEmptyString(dropoffCity?.name) ??
    coerceNonEmptyString(salesOrderCityRel?.name) ??
    coerceNonEmptyString(soMeta?.cityName) ??
    undefined

  const cityId =
    dropoffCity?.documentId != null || dropoffCity?.id != null
      ? String(dropoffCity?.documentId ?? dropoffCity?.id)
      : salesOrderCityRel?.documentId != null || salesOrderCityRel?.id != null
        ? String(salesOrderCityRel?.documentId ?? salesOrderCityRel?.id)
        : undefined

  // Sales order ID
  const salesOrderIdValue = salesOrderId ||
    String(salesOrder?.documentId ?? salesOrder?.id ?? '')

  // Bag weight (kg per bag from variant; null for bulk or when not populated)
  const bagWeightKg = variant?.weight != null ? parseNumber(variant.weight) || null : null

  // Delivered qty in tons: sum of accepted DeliveryLine.deliveredQuantity × variant.weight (kg/bag) / 1000
  // deliveredQuantityFromLines is injected by the export controller; null for regular list/detail calls
  const rawDeliveredQty = parseNumber(
    rawEntity?.deliveredQuantityFromLines ??
    (rawEntity?.attributes as Record<string, unknown> | undefined)?.deliveredQuantityFromLines
  )
  const deliveredQtyTons =
    rawDeliveredQty > 0 && bagWeightKg != null && bagWeightKg > 0
      ? Math.round((rawDeliveredQty * bagWeightKg / 1000) * 1000) / 1000
      : null

  const notes = entity.cancellationReason
    ? `Cancelled: ${entity.cancellationReason}`
    : (metadata.notes as string | undefined)
  const driverName =
    typeof rawDriverNameFromEntity === 'string' && rawDriverNameFromEntity.trim().length > 0
      ? rawDriverNameFromEntity.trim()
      : undefined
  const vehicleLicensePlate = rawVehicleLicensePlateFromEntity

  const sapSalesOrderNumber = coerceNonEmptyString(
    (rawEntity?.sapSalesOrderNumber ?? (rawEntity?.attributes as Record<string, unknown> | undefined)?.sapSalesOrderNumber) ??
    entity.sapSalesOrderNumber
  ) ?? null

  const salesOrderNumber = coerceNonEmptyString(
    (salesOrder?.salesOrderNumber as string | undefined) ??
    ((salesOrder?.attributes as Record<string, unknown> | undefined)?.salesOrderNumber as string | undefined)
  ) ?? undefined

  return {
    id,
    numericId: typeof entity.id === 'number' ? entity.id : undefined,
    orderNumber,
    sapSalesOrderNumber: sapSalesOrderNumber || undefined,
    salesOrderNumber,
    customerId: String(tenant?.id ?? tenant?.documentId ?? 'unknown'),
    contractId: salesOrderIdValue || undefined,
    items: [
      {
        productId: String(variant?.id ?? variant?.documentId ?? sku?.id ?? 'product'),
        product: productLocalized,
        quantity: { value: quantityValue, unit },
        unitPrice: { amount: unitPriceAmount, currency: DEFAULT_CURRENCY },
        totalPrice: { amount: quantityValue * unitPriceAmount, currency: DEFAULT_CURRENCY },
        bagWeightKg,
      },
    ],
    status: mapApiStatusToDomain(String(entity.orderStatus ?? 'planned')),
    processingStartedAt: coerceNonEmptyString(entity.processingStartedAt) ?? undefined,
    orderDate,
    deliveryDate,
    totalAmount: { amount: quantityValue * unitPriceAmount, currency: DEFAULT_CURRENCY },
    deliveryAddress,
    deliveryAddressId,
    dropoffCity: dropoffCityName,
    cityId,
    notes,
    tenant: tenant?.name && tenant?.code
      ? { name: String(tenant.name), code: String(tenant.code) }
      : undefined,
    tenantDocumentId: tenant?.documentId ? String(tenant.documentId) : null,
    factoryId: factory?.documentId != null || factory?.id != null
      ? String(factory?.documentId ?? factory?.id)
      : undefined,
    factoryName: typeof factory?.name === 'string' ? factory.name : undefined,
    driverName,
    vehicleLicensePlate,
    processingToDeliveredSeconds: toNullableNonNegativeInteger(rawProcessingToDeliveredSeconds),
    loadingDurationSeconds: toNullableNonNegativeInteger(rawLoadingDurationSeconds),
    exitedFactoryAt: coerceNonEmptyString(rawExitedFactoryAt as string | null | undefined) ?? null,
    loadingLocationLabel: coerceNonEmptyString(rawLoadingLocationLabel as string | null | undefined) ?? null,
    deliveredQtyTons,
    subCustomerId: extractSubCustomerId(rawSubCustomer),
    subCustomerDocumentId: extractSubCustomerDocumentId(rawSubCustomer),
    subCustomerName: extractSubCustomerName(rawSubCustomer),
    subCustomerPhone: extractSubCustomerPhone(rawSubCustomer),
    subCustomerCode: extractSubCustomerCode(rawSubCustomer),
    createdAt: String(entity.createdAt ?? ''),
    updatedAt: String(entity.updatedAt ?? entity.createdAt ?? ''),
    salesOrderCreatedByUser: (() => {
      const raw = normalizeRelation<Record<string, unknown>>(salesOrder?.createdByUser)
      if (!raw?.id) return undefined
      return {
        id: Number(raw.id),
        username: typeof raw.username === 'string' ? raw.username : undefined,
        email: typeof raw.email === 'string' ? raw.email : undefined,
        fullName: typeof raw.fullName === 'string' ? raw.fullName : undefined,
      }
    })(),
  }
}

export function transformOrdersResponseToDomain(
  raw: unknown[],
  salesOrderId?: string
): Order[] {
  return raw.map(item => transformOrderResponseToDomain(item, salesOrderId))
}

// ============================================
// Stats Transformer
// ============================================

export function computeStats(orders: Order[]): OrderStats {
  return {
    pending: orders.filter(o => o.status === 'pending').length,
    processing: orders.filter(o => o.status === 'processing').length,
    delivered: orders.filter(o => o.status === 'delivered').length,
    total: orders.length,
  }
}

// ============================================
// Filter Transformer
// ============================================

export function transformFilterToParams(filter?: OrdersFilter): URLSearchParams {
  const params = new URLSearchParams()

  if (filter?.status && filter.status.length > 0) {
    const apiStatuses = filter.status.map((status) =>
      status === 'pending' ? 'planned' : status === 'cancelled' ? 'canceled' : status
    )

    if (apiStatuses.length === 1) {
      params.set('filters[orderStatus][$eq]', apiStatuses[0])
    } else {
      apiStatuses.forEach((status, index) => {
        params.set(`filters[orderStatus][$in][${index}]`, status)
      })
    }
  }

  if (filter?.customerId) {
    const isNumeric = /^[0-9]+$/.test(filter.customerId.trim())
    if (isNumeric) {
      params.set('filters[tenant][id][$eq]', filter.customerId)
    } else {
      params.set('filters[tenant][documentId][$eq]', filter.customerId)
    }
  }

  if (filter?.dateFrom) {
    params.set('filters[createdAt][$gte]', new Date(`${filter.dateFrom}T00:00:00`).toISOString())
  }

  if (filter?.dateTo) {
    params.set('filters[createdAt][$lte]', new Date(`${filter.dateTo}T23:59:59.999`).toISOString())
  }

  return params
}
